import React from "react";
import { useTranslation } from "../../core/translation.core";
import productCore from "../../core/product.core";
import { getPointCostForCartItem, isCartItemPointRedeemed } from "../../core/pointRedemption.core";
import { CartItem } from "../../types/cart";
import { Product } from "../../types/product";
import { useGuru } from "../context/guruContext";

export function PointRedemptionBadge() {
  const { t, msg } = useTranslation();

  return (
    <span
      className="inline-flex items-center px-2 py-0.5 text-xs font-medium text-white bg-guru-calmingBlueGreen rounded flex-shrink-0"
      data-point-redemption-badge
    >
      {t(msg.cart.pointRedemptionBadge)}
    </span>
  );
}

interface PointRedemptionNoteProps {
  product: Product;
  quantity: number;
}

export function PointRedemptionNote({ product, quantity }: PointRedemptionNoteProps) {
  const { t, msg } = useTranslation();

  if (!productCore.isPointRedeemable(product)) return null;

  const points = getPointCostForCartItem(product, quantity);

  return (
    <p className="text-sm text-guru-milk-chocolate font-medium" data-point-cost-note={points}>
      {t(msg.cart.paidWithPoints).replace("{points}", String(points))}
    </p>
  );
}

export function ProductListingPointRedemptionNote({ product }: { product: Product }) {
  const { t, msg } = useTranslation();

  if (!productCore.isPointRedeemable(product)) return null;

  const points = product.pointRedemption!.pointCost;

  return (
    <span className="text-xs text-guru-calmingBlueGreen font-medium" data-point-cost-note={points}>
      {t(msg.product.redeemWithPoints).replace("{points}", String(points))}
    </span>
  );
}

export function ProductDetailPointRedemptionInfo({ product }: { product: Product }) {
  const { t, msg } = useTranslation();
  const { profileData, isLoggedIn } = useGuru();

  if (!productCore.isPointRedeemable(product)) return null;

  const points = product.pointRedemption!.pointCost;

  return (
    <div className="flex flex-col gap-1 mt-2" data-point-redemption-info>
      <div className="flex items-center gap-2">
        <PointRedemptionBadge />
        <span className="text-sm text-guru-calmingBlueGreen font-medium" data-point-cost-note={points}>
          {t(msg.product.redeemWithPoints).replace("{points}", String(points))}
        </span>
      </div>
      {isLoggedIn && profileData ? (
        <span className="text-xs text-gray-500">
          {t(msg.account.availableDollar)} {profileData.userRewardPoints}
        </span>
      ) : null}
    </div>
  );
}

interface PointRedemptionNoteForCartItemProps {
  item: CartItem;
  quantity?: number;
}

export function PointRedemptionNoteForCartItem({ item, quantity = item.quantity }: PointRedemptionNoteForCartItemProps) {
  if (!isCartItemPointRedeemed(item)) return null;

  return <PointRedemptionNote product={item.product} quantity={quantity} />;
}

interface PointRedeemSummaryRowProps {
  pointRedeemRequired?: number;
}

export function PointRedeemSummaryRow({ pointRedeemRequired }: PointRedeemSummaryRowProps) {
  const { t, msg } = useTranslation();

  if (!pointRedeemRequired || pointRedeemRequired <= 0) return null;

  return (
    <div id="point-redeem-required" className="flex justify-between items-center mb-2 text-base">
      <span className="text-sm text-gray-600">{t(msg.checkout.pointRedeemRequired)}</span>
      <span className="font-medium">
        {pointRedeemRequired} {t(msg.checkout.point)}
      </span>
    </div>
  );
}

interface PointRedeemErrorMessageProps {
  errorCode?: string;
}

export function PointRedeemErrorMessage({ errorCode }: PointRedeemErrorMessageProps) {
  const { t, msg } = useTranslation();
  const { profileData, isLoggedIn } = useGuru();

  if (!errorCode) return null;

  let message = "";
  if (errorCode === "pointRedeemInsufficient") {
    const availablePoints = isLoggedIn && profileData ? profileData.userRewardPoints : 0;
    message = t(msg.checkout.pointRedeemInsufficient).replace("{available}", String(availablePoints));
  } else if (errorCode === "pointRedeemMaxPerOrderExceeded") message = t(msg.checkout.pointRedeemMaxPerOrderExceeded);
  else if (errorCode === "pointRedeemMaxPerUserExceeded") message = t(msg.checkout.pointRedeemMaxPerUserExceeded);
  else return null;

  return (
    <div id="point-redeem-error" className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-600 text-sm">
      {message}
    </div>
  );
}
